import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { useMedia, useReduceMotion } from '../../hooks/useMotionPrefs';
import { Icon, type IconName } from './Icon';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'sm' | 'md' | 'lg';
  icon?: IconName;
  loading?: boolean;
  block?: boolean;
}

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-accent text-accent-ink shadow-[0_10px_24px_-12px_var(--c-accent)] hover:bg-accent-strong disabled:opacity-50',
  secondary:
    'bg-surface-raised text-ink border border-line hover:border-accent/50 disabled:opacity-50',
  ghost: 'bg-transparent text-ink-muted hover:text-ink hover:bg-white/5 disabled:opacity-40',
  danger: 'bg-danger/15 text-danger border border-danger/40 hover:bg-danger/25 disabled:opacity-50',
};

const SIZES = {
  sm: 'h-9 px-3 text-sm gap-1.5 rounded-xl',
  md: 'h-11 px-4 text-[15px] gap-2 rounded-2xl',
  lg: 'h-14 px-5 text-base gap-2.5 rounded-2xl',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    icon,
    loading,
    block,
    className,
    children,
    disabled,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      className={clsx(
        'press inline-flex select-none items-center justify-center font-bold tracking-tight transition-colors duration-150',
        VARIANTS[variant],
        SIZES[size],
        block && 'w-full',
        className,
      )}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Spinner /> : icon ? <Icon name={icon} size={size === 'sm' ? 16 : 18} /> : null}
      {children}
    </button>
  );
});

export function IconButton({
  icon,
  label,
  className,
  ...rest
}: { icon: IconName; label: string } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      aria-label={label}
      title={label}
      className={clsx(
        'press inline-flex h-11 w-11 items-center justify-center rounded-2xl text-ink-muted transition-colors duration-150 hover:bg-white/5 hover:text-ink',
        className,
      )}
      {...rest}
    >
      <Icon name={icon} />
    </button>
  );
}

export function Spinner({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className="animate-spin" aria-hidden="true">
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="3"
        fill="none"
      />
      <path
        d="M21 12a9 9 0 00-9-9"
        stroke="currentColor"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <label className="flex min-h-12 cursor-pointer items-center justify-between gap-4 py-2">
      <span className="min-w-0">
        <span className="block text-[15px] font-semibold text-ink">{label}</span>
        {description && <span className="block text-sm text-ink-muted">{description}</span>}
      </span>
      <SwitchPrimitive.Root
        checked={checked}
        onCheckedChange={onChange}
        className="relative h-7 w-12 shrink-0 rounded-full border border-line bg-surface-muted transition-colors duration-200 data-[state=checked]:border-accent data-[state=checked]:bg-accent"
      >
        <SwitchPrimitive.Thumb className="block h-5 w-5 translate-x-1 rounded-full bg-ink shadow transition-transform duration-200 ease-[var(--ease-out)] data-[state=checked]:translate-x-6 data-[state=checked]:bg-accent-ink" />
      </SwitchPrimitive.Root>
    </label>
  );
}

export function Segmented<T extends string | number>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: ReactNode; hint?: string }[];
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="flex gap-1 rounded-2xl border border-line bg-surface-muted p-1"
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={String(opt.value)}
            role="radio"
            aria-checked={active}
            title={opt.hint}
            onClick={() => onChange(opt.value)}
            className={clsx(
              'press relative h-10 flex-1 rounded-xl px-2 text-sm font-bold transition-colors duration-150',
              active ? 'text-accent-ink' : 'text-ink-muted hover:text-ink',
            )}
          >
            {active && (
              <motion.span
                layoutId={`seg-${label}`}
                className="absolute inset-0 rounded-xl bg-accent"
                transition={{ type: 'spring', duration: 0.3, bounce: 0.1 }}
              />
            )}
            <span className="relative">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <div className="space-y-2">
      <div className="text-xs font-bold uppercase tracking-[0.14em] text-ink-faint">{label}</div>
      {children}
      {hint && <p className="text-sm leading-snug text-ink-muted">{hint}</p>}
    </div>
  );
}

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={clsx(
        'h-12 w-full rounded-2xl border border-line bg-surface-muted px-4 text-base text-ink placeholder:text-ink-faint focus:border-accent focus:outline-none',
        props.className,
      )}
    />
  );
}

/**
 * Bottom sheet on phones, centred dialog on wider screens. Radix handles focus trapping, escape and
 * aria; Motion handles the spring entrance (exits the way it entered).
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  dismissible = true,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  dismissible?: boolean;
}) {
  const reduce = useReduceMotion();
  const wide = useMedia('(min-width: 640px)');
  // Phones: a bottom sheet that exits the way it entered. Wider screens: a centred dialog (not anchored
  // to a trigger, so it scales from centre).
  const hidden = reduce
    ? { opacity: 0 }
    : wide
      ? { opacity: 0, transform: 'scale(0.96)' }
      : { transform: 'translateY(100%)' };
  const shown = reduce
    ? { opacity: 1 }
    : wide
      ? { opacity: 1, transform: 'scale(1)' }
      : { transform: 'translateY(0%)' };
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => (dismissible || o) && onOpenChange(o)}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content
              asChild
              forceMount
              onInteractOutside={(e) => !dismissible && e.preventDefault()}
              onEscapeKeyDown={(e) => !dismissible && e.preventDefault()}
            >
              <motion.div
                className="panel fixed inset-x-0 bottom-0 z-50 mx-auto max-h-[88dvh] w-full max-w-lg overflow-y-auto rounded-t-[28px] px-5 pb-[calc(20px+var(--safe-bottom))] pt-3 sm:bottom-auto sm:top-[12dvh] sm:rounded-[28px] sm:pb-6"
                initial={hidden}
                animate={shown}
                exit={hidden}
                transition={
                  wide
                    ? { duration: 0.2, ease: [0.23, 1, 0.32, 1] }
                    : { type: 'spring', duration: 0.42, bounce: 0.06 }
                }
              >
                <div
                  className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-white/15 sm:hidden"
                  aria-hidden="true"
                />
                <div className="mb-4 flex items-start justify-between gap-4">
                  <div>
                    <DialogPrimitive.Title className="font-display text-2xl text-ink">
                      {title}
                    </DialogPrimitive.Title>
                    {description ? (
                      <DialogPrimitive.Description className="mt-1 text-sm text-ink-muted">
                        {description}
                      </DialogPrimitive.Description>
                    ) : (
                      <DialogPrimitive.Description className="sr-only">
                        {title}
                      </DialogPrimitive.Description>
                    )}
                  </div>
                  {dismissible && (
                    <DialogPrimitive.Close asChild>
                      <IconButton icon="close" label="Close" className="-mr-2 -mt-1" />
                    </DialogPrimitive.Close>
                  )}
                </div>
                {children}
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}

const AVATAR_HUES: Record<string, number> = {
  peacock: 190,
  lotus: 330,
  elephant: 220,
  tiger: 28,
  mango: 45,
  kite: 270,
  diya: 15,
  tabla: 120,
};

export function Avatar({
  name,
  avatarId,
  size = 44,
  bot,
}: {
  name: string;
  avatarId: string;
  size?: number;
  bot?: boolean;
}) {
  const hue = AVATAR_HUES[avatarId] ?? (avatarId.charCodeAt(avatarId.length - 1) * 47) % 360;
  const initial = [...(name.trim() || '?')][0]!.toUpperCase();
  return (
    <span
      className="relative inline-flex shrink-0 items-center justify-center rounded-full font-extrabold text-white"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.42,
        background: `radial-gradient(circle at 30% 25%, hsl(${hue} 70% 62%), hsl(${hue} 55% 34%))`,
        boxShadow: 'inset 0 -2px 6px rgba(0,0,0,0.35), 0 4px 10px -4px rgba(0,0,0,0.6)',
      }}
      aria-hidden="true"
    >
      {bot ? <Icon name="bot" size={size * 0.5} /> : initial}
    </span>
  );
}

export function TeamGlyph({ team, size = 10 }: { team: 0 | 1; size?: number }) {
  // Shape + colour, so team identity never relies on colour alone.
  return team === 0 ? (
    <svg width={size} height={size} viewBox="0 0 10 10" aria-hidden="true">
      <circle cx="5" cy="5" r="4.2" fill="var(--c-team-a)" />
    </svg>
  ) : (
    <svg width={size} height={size} viewBox="0 0 10 10" aria-hidden="true">
      <path d="M5 0.6L9.4 5 5 9.4 0.6 5z" fill="var(--c-team-b)" />
    </svg>
  );
}
